/**
 * Compliance → Sensitive-PI tier register - Phase 8 batch G item 5.
 *
 * CCPA §1798.121 + GDPR Art 9 crossover. Tenant-side policy declaration
 * per category: in-scope, retention, consent-required vs lawful-basis
 * override, processing restrictions, geo restrictions.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, Plus, Eye, Lock, LockOpen,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listSensitivePiTiers, createSensitivePiTier,
  type SensitivePiTier, type SensitivePiLawfulBasis,
} from '@/lib/api';

const SUGGESTED_CATEGORIES: ReadonlyArray<string> = [
  'aadhaar', 'pan', 'minor_dob', 'biometric', 'health', 'genetic',
  'sexual_orientation', 'religious', 'political', 'financial_account',
  'precise_geolocation',
];
const LAWFUL_BASES: ReadonlyArray<{ value: SensitivePiLawfulBasis; label: string }> = [
  { value: 'art9_2_b_employment', label: 'Art 9(2)(b) - Employment / social-security law' },
  { value: 'art9_2_c_vital', label: 'Art 9(2)(c) - Vital interests of subject' },
  { value: 'art9_2_g_substantial_public', label: 'Art 9(2)(g) - Substantial public interest' },
  { value: 'art9_2_h_health', label: 'Art 9(2)(h) - Healthcare / occupational medicine' },
  { value: 'art9_2_i_public_health', label: 'Art 9(2)(i) - Public health' },
  { value: 'art9_2_j_research', label: 'Art 9(2)(j) - Research / archiving' },
];

export function ComplianceSensitivePi(): JSX.Element {
  const [items, setItems] = useState<SensitivePiTier[]>([]);
  const [counts, setCounts] = useState({ inScope: 0, outOfScope: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<SensitivePiTier | null>(null);

  const [category, setCategory] = useState('');
  const [inScope, setInScope] = useState(true);
  const [retention, setRetention] = useState('');
  const [consentReq, setConsentReq] = useState(true);
  const [lawfulBasis, setLawfulBasis] = useState<SensitivePiLawfulBasis | ''>('');
  const [geo, setGeo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await listSensitivePiTiers({}); setItems(r.tiers); setCounts(r.counts); }
    catch (e) { setError(e instanceof Error ? e.message : 'failed to load tiers'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function handleCreate() {
    if (!category.trim()) { setError('Category required'); return; }
    if (!consentReq && !lawfulBasis) {
      setError('Lawful basis override required when consent is not required');
      return;
    }
    if (consentReq && lawfulBasis) {
      setError('Cannot set lawful basis override when consent is required');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      await createSensitivePiTier({
        category: category.trim().toLowerCase(),
        inScope,
        consentRequired: consentReq,
        ...(retention.trim() ? { retentionDays: parseInt(retention, 10) } : {}),
        ...(lawfulBasis ? { lawfulBasisOverride: lawfulBasis } : {}),
        ...(geo.trim() ? { geoRestrictions: geo.split(',').map((g) => g.trim()).filter(Boolean) } : {}),
      });
      setCategory(''); setRetention(''); setGeo(''); setLawfulBasis(''); setConsentReq(true); setInScope(true);
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'create failed'); }
    finally { setSubmitting(false); }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Sensitive-PI tier register (CCPA §1798.121 / GDPR Art 9)"
        subtitle="Per-tenant policy for each sensitive-PI category: in-scope flag, retention window, consent-required vs lawful-basis override, processing + geo restrictions."
        badges={[
          { label: '§1798.121 + Art 9', variant: 'glass' },
          { label: 'Drives coverage', variant: 'glass' },
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Load error', variant: 'warning' as const }]
            : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={<Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>}
      />
      {error && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
          <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <KpiCard label="In scope" value={counts.inScope} icon={Lock} glow={counts.inScope > 0 ? 'high' : 'low'} hint="active policy" />
        <KpiCard label="Out of scope" value={counts.outOfScope} icon={LockOpen} glow="low" hint="documented exemption" />
        <KpiCard label="Total declared" value={counts.inScope + counts.outOfScope} icon={CheckCircle2} glow="low" hint="categories on file" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Declare a category</CardTitle>
            <CardDescription>One row per (tenant, category). Consent-required is the Art 9(2)(a) default - flip off to document an alternative lawful basis.</CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div><label className="text-muted-foreground block mb-0.5">Category</label>
              <input type="text" value={category} onChange={(e) => setCategory(e.target.value)}
                list="sensitive-pi-suggested" placeholder="e.g. aadhaar"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" />
              <datalist id="sensitive-pi-suggested">
                {SUGGESTED_CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
              <div className="text-[10px] text-muted-foreground mt-0.5">Lowercased on submit. Drives Discovery + Coverage joins.</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[11px]">
              <label className="flex items-center gap-1"><input type="checkbox" checked={inScope} onChange={(e) => setInScope(e.target.checked)} /> In scope</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={consentReq} onChange={(e) => { setConsentReq(e.target.checked); if (e.target.checked) setLawfulBasis(''); }} /> Consent required (Art 9(2)(a))</label>
            </div>
            <div><label className="text-muted-foreground block mb-0.5">Retention (days, blank = indefinite)</label>
              <input type="number" min="1" value={retention} onChange={(e) => setRetention(e.target.value)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            {!consentReq && (
              <div>
                <label className="text-muted-foreground block mb-0.5">Lawful basis override (required when consent unchecked)</label>
                <select value={lawfulBasis} onChange={(e) => setLawfulBasis(e.target.value as SensitivePiLawfulBasis | '')}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                  <option value="">- select -</option>
                  {LAWFUL_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
            )}
            <div><label className="text-muted-foreground block mb-0.5">Geo restrictions (comma)</label>
              <input type="text" value={geo} onChange={(e) => setGeo(e.target.value)}
                placeholder="no_us_transfer, eea_only"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_sensitive.a01" onClick={() => void handleCreate()} disabled={submitting}>
                {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
                Declare
              </Button>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3"><CardTitle>Sensitive-PI tier register</CardTitle>
            <CardDescription clamp>Auditor sees this as your "what data you process + how" declaration.</CardDescription></CardHeader>
          {loading && <LoadingState caption="Loading…" />}
          {!loading && items.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Lock className="h-6 w-6 mx-auto mb-2 opacity-40" /><div>No sensitive-PI categories declared.</div>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2">
              {items.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }} className="glass-lite rounded p-3 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={t.inScope ? 'success' : 'glass'} className="text-[10px]">{t.inScope ? 'in scope' : 'out of scope'}</Badge>
                        <span className="font-medium">{t.category}</span>
                        {t.consentRequired
                          ? <Badge variant="glass" className="text-[10px]">consent (Art 9(2)(a))</Badge>
                          : <Badge variant="warning" className="text-[10px]">{t.lawfulBasisOverride?.replace(/_/g, ' ')}</Badge>}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        retention: {t.retentionDays ? `${t.retentionDays} day(s)` : 'indefinite'}
                        {t.geoRestrictions.length > 0 && ` · geo: ${t.geoRestrictions.join(', ')}`}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="glass" onClick={() => setDetail(t)}><Eye className="h-3 w-3" /></Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
      <Dialog open={detail !== null} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{detail?.category ?? ''}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">In scope</div><Badge variant={detail.inScope ? 'success' : 'glass'} className="text-[10px]">{String(detail.inScope)}</Badge></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Retention</div><code className="font-mono">{detail.retentionDays ? `${detail.retentionDays}d` : '-'}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Consent required</div><Badge variant={detail.consentRequired ? 'success' : 'warning'} className="text-[10px]">{String(detail.consentRequired)}</Badge></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Lawful basis override</div><code className="font-mono">{detail.lawfulBasisOverride ?? '-'}</code></div>
              </div>
              {detail.geoRestrictions.length > 0 && (
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Geo restrictions</div><div className="flex flex-wrap gap-1">{detail.geoRestrictions.map((g) => <Badge key={g} variant="glass" className="text-[10px]">{g}</Badge>)}</div></div>
              )}
              {detail.processingRestrictions.length > 0 && (
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Processing restrictions</div>
                  {detail.processingRestrictions.map((r) => (
                    <div key={r.id} className="text-[11px]">{r.label}{r.enforcedBy && <em> · enforced by {r.enforcedBy}</em>}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
